import {
  analyzeCategory,
  debugCategoryDecision,
} from '../../../utils/categoryClassifier';
import { isDebugFlagEnabled } from '../../../utils/debugTools';
import {
  getSchoolImportConfidence,
  hasStrongSchoolStructureForCategory,
  isSchoolIntroOrClosing,
  resolveChunkCategory,
  shouldKeepImportedSchoolRow,
} from './importLogsParsingClassification';
import {
  buildImportRowFromChunk,
  resolveInheritedChunkTime,
  shouldMergeWithPreviousImportedRow,
} from './importLogsParsingChunks';
import {
  buildChunkImportText,
  extractTimeFromText,
  inferApproximateTimeFromContext,
  resolveChunkTime,
} from './importLogsParsingTime';
import { getLeadingImportDateHeading } from './importLogsDateContext';
import { normalizeImportedSchoolReviewRows } from './importLogsReviewNormalization';
import { buildNaturalCaptureChunkPlan } from './naturalCaptureChunkingCore';
import { runNaturalCaptureSchoolAdapter } from './naturalCaptureSchoolAdapter';
import {
  CARE_DATE_MONTH_NAME_PATTERN,
  createCareTimelineHeadingRegex,
  normalizeCareDateTimeText,
} from '../../../shared/careDateTimePatterns';

const CAREGIVER_TIMELINE_MONTH_PATTERN = CARE_DATE_MONTH_NAME_PATTERN;
const CAREGIVER_TIMELINE_STRONG_ANCHOR_PATTERN = createCareTimelineHeadingRegex({ global: true, requireTime: true });
const CAREGIVER_INLINE_STATUS_DATE_PATTERN = new RegExp(
  `\\b(?:stopped|started|increased|decreased)\\s+(?:on\\s+)?${CAREGIVER_TIMELINE_MONTH_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?\\b`,
  'i'
);

const isImportParserDebugEnabled = () => {
  return isDebugFlagEnabled('debugImportParser');
};

const debugImportParser = (label, payload) => {
  if (!isImportParserDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(`[ImportParser] ${label}`, payload);
};

const normalizeCaregiverTimelineLine = (line = '') => normalizeCareDateTimeText(
  String(line || '').replace(/\r/g, '')
);

const splitLineByRepeatedCaregiverTimelineAnchors = (line = '') => {
  const normalizedLine = normalizeCaregiverTimelineLine(line);
  if (!normalizedLine) {
    return [];
  }

  const matches = Array.from(normalizedLine.matchAll(CAREGIVER_TIMELINE_STRONG_ANCHOR_PATTERN))
    .map((match) => ({
      index: Number(match?.index || 0),
      text: String(match?.[0] || '').trim(),
    }))
    .filter((match) => match.text);

  if (matches.length <= 1) {
    return [normalizedLine];
  }

  const startIndices = matches
    .filter((match, index) => {
      if (index === 0) {
        return true;
      }
      const previousCharacter = normalizedLine.charAt(Math.max(0, match.index - 1));
      return !previousCharacter || /[\s,.;:!?()\-–—]/u.test(previousCharacter);
    })
    .map((match) => match.index);

  if (startIndices.length <= 1) {
    return [normalizedLine];
  }

  return startIndices.map((startIndex, index) => {
    const endIndex = index + 1 < startIndices.length ? startIndices[index + 1] : normalizedLine.length;
    return normalizedLine.slice(startIndex, endIndex).trim();
  }).filter(Boolean);
};

const buildCaregiverTimelinePreSegmentedText = (text = '', { defaultDate = new Date() } = {}) => {
  const rawText = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!rawText) {
    return {
      text: '',
      applied: false,
    };
  }

  const fallbackDate = defaultDate instanceof Date ? defaultDate : new Date(defaultDate);
  const defaultYear = Number.isFinite(fallbackDate?.getTime?.())
    ? fallbackDate.getFullYear()
    : new Date().getFullYear();
  const lines = rawText
    .split('\n')
    .flatMap((line) => splitLineByRepeatedCaregiverTimelineAnchors(line))
    .map((line) => normalizeCaregiverTimelineLine(line))
    .filter(Boolean);

  if (!lines.length) {
    return {
      text: rawText,
      applied: false,
    };
  }

  let dateSignalCount = 0;
  let pendingAnchor = null;
  const segments = [];

  lines.forEach((line) => {
    const heading = getLeadingImportDateHeading(line, {
      defaultYear,
      sourceText: rawText,
    });
    const isDayPrecisionHeading = Boolean(heading?.date && heading?.datePrecision === 'day');
    const isInlineStatusDate = CAREGIVER_INLINE_STATUS_DATE_PATTERN.test(line);

    if (isDayPrecisionHeading) {
      dateSignalCount += 1;
      if (heading.bodyText) {
        const timePrefix = normalizeCaregiverTimelineLine(String(heading.timeLabel || heading.timeValue || '').trim());
        segments.push([timePrefix, heading.bodyText].filter(Boolean).join(' ').trim());
        pendingAnchor = null;
      } else {
        pendingAnchor = {
          headingText: heading.headingText || line,
          timePrefix: normalizeCaregiverTimelineLine(String(heading.timeLabel || heading.timeValue || '').trim()),
        };
      }
      return;
    }

    if (pendingAnchor) {
      segments.push([pendingAnchor.timePrefix, line].filter(Boolean).join(' ').trim());
      pendingAnchor = null;
      return;
    }

    if (isInlineStatusDate) {
      dateSignalCount += 1;
    }

    segments.push(line);
  });

  if (pendingAnchor) {
    segments.push([pendingAnchor.timePrefix, pendingAnchor.headingText].filter(Boolean).join(' ').trim());
  }

  if (dateSignalCount < 2 || segments.length <= 1) {
    return {
      text: rawText,
      applied: false,
    };
  }

  return {
    text: segments.join('\n\n'),
    applied: true,
  };
};

const buildTimelinePreSegmentedChunks = (text = '') => String(text || '')
  .split(/\n{2,}/)
  .map((segment) => String(segment || '').trim())
  .filter(Boolean)
  .map((segment) => ({
    text: segment,
    time: extractTimeFromText(segment).time || null,
    category: null,
  }));

/**
 * Compatibility wrapper only.
 * New parsing logic should go through parseNaturalCaptureText().
 */
export const buildNaturalCaptureSchoolRows = (text = '', { defaultDate = new Date(), childName = null } = {}) => {
  const rawText = String(text || '').trim();
  if (!rawText) return [];

  const {
    text: segmentedText,
    applied: timelineStructuredImport,
  } = buildCaregiverTimelinePreSegmentedText(rawText, { defaultDate });

  const chunkPlan = timelineStructuredImport
    ? {
        inputShape: 'school_text',
        chunkerUsed: 'buildTimelinePreSegmentedChunks',
        chunks: buildTimelinePreSegmentedChunks(segmentedText),
      }
    : buildNaturalCaptureChunkPlan(segmentedText, {
        inputShape: 'school_text',
      });
  const chunks = chunkPlan.chunks;
  if (!chunks.length) {
    debugImportParser('done', { rowCount: 0, times: [], categories: [] });
    return [];
  }

  debugImportParser('start', {
    rawLength: rawText.length,
    segmentedLength: segmentedText.length,
    chunkCount: chunks.length,
    defaultDate: defaultDate instanceof Date ? defaultDate.toString() : defaultDate,
    childName,
  });

  const rows = chunks.reduce((state, chunk, index) => {
    const resolvedTime = resolveChunkTime(chunk);
    const { extracted, cleanText } = buildChunkImportText(chunk);
    const {
      fallbackAnalysis,
      category,
      categorySource,
      categoryDecision,
    } = resolveChunkCategory(chunk, cleanText);
    const effectiveCategory = category || (timelineStructuredImport ? 'log' : null);
    const approximateTime = inferApproximateTimeFromContext(cleanText);
    const { finalTime, timeSource } = resolveInheritedChunkTime({
      resolvedTime,
      extractedTime: extracted.time,
      approximateTime,
      category: effectiveCategory,
      cleanText,
      state,
    });
    const confidenceState = getSchoolImportConfidence({
      cleanText,
      category: effectiveCategory || category,
      categorySource,
      timeSource,
      chunkText: chunk.text,
    });
    const strongStructure = hasStrongSchoolStructureForCategory(effectiveCategory || category, cleanText);
    const parsedObservation = confidenceState.confidence === 'review' && !strongStructure;
    const keepRow = timelineStructuredImport
      ? Boolean(cleanText)
      : shouldKeepImportedSchoolRow({ cleanText, category: effectiveCategory, confidenceState, chunk });

    if (!cleanText || isSchoolIntroOrClosing(cleanText) || !effectiveCategory) {
      return state;
    }

    const prevRow = timelineStructuredImport
      ? null
      : shouldMergeWithPreviousImportedRow({
          state,
          category: effectiveCategory,
          resolvedTime,
          extractedTime: extracted.time,
          approximateTime,
          cleanText,
        });

    if (prevRow) {
      const mergedNote = `${prevRow.note} ${cleanText}`.trim();
      const updatedRows = state.rows.slice(0, -1).concat({ ...prevRow, note: mergedNote });
      debugImportParser('merge', { into: prevRow.note.slice(0, 60), added: cleanText.slice(0, 60) });
      return {
        lastResolvedTime: prevRow.localTime || state.lastResolvedTime,
        lastCategory: effectiveCategory,
        rows: updatedRows,
      };
    }

    if (!keepRow) {
      return state;
    }

    const row = buildImportRowFromChunk({
      index,
      defaultDate,
      childName,
      category: effectiveCategory,
      cleanText,
      finalTime,
      timeSource,
      categorySource,
      confidenceState,
      parsedObservation,
    });
    const timelineRow = timelineStructuredImport
      ? {
          ...row,
          timelineStructuredImport: true,
          reviewConfidence: row?.reviewConfidence === 'review' ? 'high' : row?.reviewConfidence,
          reviewConfidenceLabel: row?.reviewConfidence === 'review'
            ? 'Looks right'
            : row?.reviewConfidenceLabel,
          metadata: {
            ...(row?.metadata || {}),
            timelineStructuredImport: true,
          },
        }
      : row;

    const debugAnalysis = fallbackAnalysis || analyzeCategory(cleanText, { allowUnknown: true });
    debugCategoryDecision('import-row', cleanText, debugAnalysis);
    debugImportParser('row', {
      index,
      chunkText: chunk.text,
      resolvedTime,
      extractedTime: extracted.time,
      assignedTime: finalTime,
      fallbackUsed: timeSource !== 'explicit',
      category: timelineRow.category,
      categoryDecision,
      notePreview: timelineRow.note.slice(0, 120),
      timelineStructuredImport,
    });

    return {
      lastResolvedTime: finalTime || state.lastResolvedTime,
      lastCategory: effectiveCategory,
      rows: state.rows.concat(timelineRow),
    };
  }, {
    lastResolvedTime: null,
    lastCategory: null,
    rows: [],
  }).rows;

  debugImportParser('done', {
    rowCount: rows.length,
    times: rows.map((row) => row.date instanceof Date ? row.date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''),
    categories: rows.map((row) => row.category),
  });

  return normalizeImportedSchoolReviewRows(rows, {
    parserMode: 'school_text',
    sourceMode: 'school_text',
    inputMode: 'import',
    rawText,
  });
};

export const runSchoolPastedTextAdapter = (adapterInput = {}, options = {}, deps = {}) => runNaturalCaptureSchoolAdapter(adapterInput, options, {
  buildNaturalCaptureSchoolRows,
  normalizeImportedSchoolReviewRows,
  ...(deps || {}),
});
